import type { BlockInfo } from 'types/block-info';
import type { TeamMember } from 'types/team';
import type { HeadingProps } from '@leafwell/components';
import React from 'react';
import { Wrap, RichText, Paragraph } from '@leafwell/components';
import HeadingBody from './heading-body';
import Figure from './figure';
import WysiwygContent from './wysiwyg-content';

const InfoTeamMember: React.FC<
  Pick<TeamMember, 'title' | 'featuredImage' | 'jobs'>
> = ({ title, featuredImage, jobs }) => {
  return (
    <div className="flex h-40 gap-x-8">
      <Figure {...featuredImage} className="w-40 rounded-xl" />
      <div>
        <Paragraph className="text-xl lg:text-2xl xl:text-3xl mb-2">
          {title}
        </Paragraph>
        <Paragraph className="text-xl lg:text-2xl xl:text-3xl text-gray-600">
          {jobs[0]?.name}
        </Paragraph>
      </div>
    </div>
  );
};

const InfoTitle: React.FC<
  Pick<BlockInfo, 'title' | 'titleSize'> & { className?: string }
> = ({ className = '', title, titleSize }) => {
  let headingLevel: HeadingProps['level'];

  if (titleSize === 'large') {
    headingLevel = '3';
  } else if (titleSize === 'xlarge') {
    headingLevel = '2';
  } else if (titleSize === 'xxlarge') {
    headingLevel = '1';
  } else {
    headingLevel = '4';
  }

  return titleSize === 'small' ? (
    <Paragraph
      size="small"
      tagName="h2"
      className={['font-medium', className].join(' ')}
      dangerouslySetInnerHTML={{ __html: title }}
    />
  ) : (
    <HeadingBody
      className={className}
      level={headingLevel}
      title={title}
      tagName={headingLevel === '4' ? 'h3' : 'h2'}
    />
  );
};

const InfoSubTitle: React.FC<
  Pick<BlockInfo, 'subtitle' | 'titleSize'> & { className?: string }
> = ({ className = '', subtitle, titleSize }) => {
  return titleSize === 'medium' ? (
    <RichText
      content={subtitle}
      className={['lg:text-lg', className].join(' ')}
    />
  ) : (
    <HeadingBody
      className={className}
      level="4"
      title={subtitle}
      tagName="div"
    />
  );
};

const Info: React.FC<BlockInfo> = ({
  sectionId,
  label,
  columns,
  titleSize,
  title,
  largerParagraph,
  content,
  subtitle,
  image,
  teamMember,
}) => {
  const hasTitle = title?.length > 0;
  const hasSubTitle = subtitle?.length > 0;
  const onlyImage = image && !hasTitle && !hasSubTitle;
  const hasTeamMember = teamMember?.length > 0;

  const halfColumns = columns === '2';
  const defaultSize = `md:col-span-${Math.floor(12 / parseInt(columns))}`;
  const columnClasses = {
    title: halfColumns
      ? onlyImage
        ? 'md:col-span-7'
        : 'md:col-span-5'
      : defaultSize,
    subtitle: defaultSize,
    content: halfColumns
      ? onlyImage
        ? 'md:col-span-5'
        : 'md:col-span-7'
      : defaultSize,
  };

  return (
    <Wrap
      id={sectionId}
      className={[
        'gap-10 xl:gap-16 md:grid-cols-12',
        onlyImage ? 'gap-y-5' : '',
      ].join(' ')}
    >
      {label?.length > 0 && (
        <HeadingBody
          className="col-span-full text-center"
          level="2"
          title={label}
        />
      )}
      {(hasTitle || image || hasTeamMember) && (
        <div className={[columnClasses.title, 'grid gap-y-10'].join(' ')}>
          {hasTitle && <InfoTitle title={title} titleSize={titleSize} />}
          {image && (
            <Figure
              {...image}
              className={['rounded-lg', hasTitle ? 'md:max-w-sm' : ''].join(
                ' ',
              )}
            />
          )}
          {hasTeamMember && <InfoTeamMember {...teamMember[0]} />}
        </div>
      )}
      {hasSubTitle && (
        <InfoSubTitle
          className={columnClasses.subtitle}
          subtitle={subtitle}
          titleSize={titleSize}
        />
      )}
      <WysiwygContent
        content={content}
        className={[
          largerParagraph ? '!text-lg' : 'lg:text-sm',
          columnClasses.content,
        ].join(' ')}
      />
    </Wrap>
  );
};

export default Info;
